import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TextSearch from '../../controls/TextSearch';
import { styled } from '@mui/material/styles';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as accountingSlice from '../../../data/accounting.slice';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TablePagination,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import { GridCellParams, GridColDef, DataGridPro } from '@mui/x-data-grid-pro';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { InvoiceStatus } from '../../../data/models/invoice-status.model';
import { ProviderInvoice } from '../../../data/models/provider-invoice.model';

const PREFIX = 'Invoices';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  cardHeader: `${PREFIX}-cardHeader`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  selectContainer: `${PREFIX}-selectContainer`,
  selectLabel: `${PREFIX}-selectLabel`,
  selectValue: `${PREFIX}-selectValue`,
  filtersButton: `${PREFIX}-filtersButton`,
  grid: `${PREFIX}-grid`,
  providerContainer: `${PREFIX}-providerContainer`,
  providerText: `${PREFIX}-providerText`,
  providerBadge: `${PREFIX}-providerBadge`,
  statusChip: `${PREFIX}-statusChip`,
  statusChipLabel: `${PREFIX}-statusChipLabel`,
  statusDate: `${PREFIX}-statusDate`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: theme.palette.lightTextPrimary.main,
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    width: '220px',
    height: '48px',
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: theme.palette.secondary.main,
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: theme.palette.lightActionDisabled90p.main,
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.selectContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  [`& .${classes.selectLabel}`]: {
    fontSize: '12px',
    color: theme.palette.lightActionDisabled60p.main,
    fontWeight: 400,
    lineHeight: '16px',
  },
  [`& .${classes.selectValue}`]: {
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.lightActionDisabled90p.main,
    lineHeight: '24px',
  },
  [`& .${classes.filtersButton}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.lightTextSecondary.main,
    borderRadius: '3px',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    padding: '12px 22px',
    textTransform: 'none',
    fontFamily: 'Source Sans Pro',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.46px',
    '&:hover': {
      backgroundColor: theme.palette.lightBackgroundPaperHover.main,
    },
    '& .MuiButton-startIcon': {
      marginRight: '8px',
    },
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '&.empty': {
      height: 300,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      width: '100%',
      color: theme.palette.lightTextPrimary.main,
      fontFamily: 'Source Sans Pro',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.17px',
      paddingLeft: 0,
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell': {
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20.02px',
      letterSpacing: '0.17px',
      color: theme.palette.lightActionDisabled90p.main,
    },
    '& .MuiDataGrid-cell--textCenter': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    [`& .MuiDataGrid-row`]: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.lightBackgroundPaperHover.main,
      },
    },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  [`& .${classes.providerContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  [`& .${classes.providerText}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.17px',
    color: theme.palette.lightActionDisabled90p.main,
  },
  [`& .${classes.providerBadge}`]: {
    height: '20px',
    minWidth: '20px',
    '& .MuiChip-label': {
      borderRadius: '50%',
      padding: '0 ' + theme.spacing(0.75),
      lineHeight: '24px',
      color: theme.palette.lightTextPrimary.main + ' !important',
      fontSize: '11px',
      fontWeight: 600,
    },
  },
  [`& .${classes.statusChip}`]: {
    borderRadius: '64px',
    height: '36px',
    padding: '6px 24px',
    '& .MuiChip-label': {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      alignItems: 'center',
      textAlign: 'center',
    },
    '&.status-under-review': {
      backgroundColor: theme.palette.lightWarningShades12p.main,
      '& .MuiChip-label': {
        color: theme.palette.lightWarningMain.main,
      },
    },
    '&.status-processing-payment': {
      backgroundColor: theme.palette.lightInfoShades12p.main,
      '& .MuiChip-label': {
        color: theme.palette.lightInfoMain.main,
      },
    },
    '&.status-paid': {
      backgroundColor: theme.palette.lightSuccessShades12p.main,
      '& .MuiChip-label': {
        color: theme.palette.lightSuccessMain.main,
      },
    },
    '&.status-collection': {
      backgroundColor: theme.palette.lightWarningShades12p.main,
      '& .MuiChip-label': {
        color: theme.palette.lightWarningMain.main,
      },
    },
  },
  [`& .${classes.statusChipLabel}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '12px',
    letterSpacing: '0.14px',
  },
  [`& .${classes.statusDate}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: '0.14px',
  },
}));

interface InvoiceData {
  id: string;
  generated: string;
  generatedDate: string;
  provider: string;
  providerBadge?: number;
  trips: number;
  amount: string;
  status: InvoiceStatus | '';
  statusDate: string;
}

const mapBillingStatusToInvoiceStatus = (
  billingStatusID: number | null,
): InvoiceStatus | '' => {
  if (billingStatusID === null) return '';
  if (billingStatusID === 0) return InvoiceStatus.Collection;
  if (billingStatusID === 1) return InvoiceStatus.Paid;
  return '';
};

const transformProviderInvoiceToInvoiceData = (
  invoice: ProviderInvoice,
): InvoiceData => {
  const invoiceDate = DateTime.fromISO(invoice.invoiceDate);
  const modifiedDate = DateTime.fromISO(invoice.modifiedOnUTC);

  return {
    id: invoice.invoiceNumber,
    generated: invoiceDate.toFormat('dd/MM/yyyy'),
    generatedDate: invoice.invoiceDate,
    provider: invoice.transportationProvider.transportationProviderName,
    trips: invoice.totalTripLegs,
    amount: invoice.invoiceAmount,
    status: mapBillingStatusToInvoiceStatus(invoice.billingStatusID),
    statusDate: modifiedDate.toFormat('MM/dd/yyyy'),
  };
};

export default function Invoices() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortModel, setSortModel] = useState([
    { field: 'generated', sort: 'desc' as 'asc' | 'desc' },
  ]);

  const providerInvoicesData = useAppSelector(
    accountingSlice.selectProviderInvoicesData,
  );
  const loading = useAppSelector(accountingSlice.selectLoadingProviderInvoices);

  useEffect(() => {
    dispatch(
      accountingSlice.fetchProviderInvoicesAsync({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      }),
    );
  }, [dispatch, searchQuery, statusFilter]);

  const onSearchChange = (val: string) => {
    setSearchQuery(val);
    setPage(1);
  };

  const renderProvider = (params: GridCellParams): React.ReactElement => {
    const row = params.row as InvoiceData;
    return (
      <Box className={classes.providerContainer}>
        <Box className={classes.providerText}>{row.provider}</Box>
        {row.providerBadge && (
          <Chip label={row.providerBadge} className={classes.providerBadge} />
        )}
      </Box>
    );
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    const row = params.row as InvoiceData;
    const getStatusClassName = (status: string) => {
      if (!status) return classes.statusChip;
      const statusLower = status.toLowerCase().replace(/\s+/g, '-');
      return `${classes.statusChip} status-${statusLower}`;
    };

    if (!row.status) {
      return <Box></Box>;
    }

    return (
      <Chip
        className={getStatusClassName(row.status)}
        label={
          <Box>
            <Box className={classes.statusChipLabel}>{row.status}</Box>
            <Box className={classes.statusDate}>{row.statusDate}</Box>
          </Box>
        }
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('invoice.id'),
      width: 88,
      sortable: false,
    },
    {
      field: 'generated',
      headerName: t('invoice.generated'),
      width: 140,
      sortable: true,
    },
    {
      field: 'provider',
      headerName: t('invoice.provider'),
      width: 140,
      sortable: false,
      renderCell: renderProvider,
    },
    {
      field: 'trips',
      headerName: t('invoice.trips'),
      width: 63,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'amount',
      headerName: t('invoice.amount'),
      width: 143,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'status',
      headerName: t('invoice.status'),
      width: 346,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: renderStatus,
    },
  ];

  const allInvoices = useMemo(() => {
    if (!providerInvoicesData?.invoices) return [];
    return providerInvoicesData.invoices.map(
      transformProviderInvoiceToInvoiceData,
    );
  }, [providerInvoicesData]);

  const sortedInvoices = useMemo(() => {
    let result = [...allInvoices];
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];

      result = [...result].sort((a, b) => {
        const aValue = a[field as keyof InvoiceData];
        const bValue = b[field as keyof InvoiceData];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sort === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return sort === 'asc'
          ? String(aValue ?? '').localeCompare(String(bValue ?? ''))
          : String(bValue ?? '').localeCompare(String(aValue ?? ''));
      });
    }

    return result;
  }, [allInvoices, sortModel]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoices = sortedInvoices.slice(startIndex, endIndex);

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <CardHeader
          title={t('navigation.invoices')}
          className={classes.cardHeader}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <TextSearch
                id="text-search"
                icon={<SearchIcon />}
                label={t('invoice.searchPlaceholder')}
                currentSearchFilter={searchQuery}
                onSearchChange={onSearchChange}
              />
              <FormControl className={classes.toolInput}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  renderValue={(value) => {
                    const getStatusLabel = (val: string) => {
                      if (val === 'all') return t('invoice.allInvoices');
                      if (val === InvoiceStatus.UnderReview)
                        return t('invoice.underReview');
                      if (val === InvoiceStatus.ProcessingPayment)
                        return t('invoice.processingPayment');
                      if (val === InvoiceStatus.Collection)
                        return t('invoice.collection');
                      if (val === InvoiceStatus.Paid) return t('invoice.paid');
                      return t('invoice.allInvoices');
                    };
                    return (
                      <Box className={classes.selectContainer}>
                        <Box className={classes.selectLabel} component="span">
                          {t('invoice.view')}
                        </Box>
                        <Box className={classes.selectValue} component="span">
                          {getStatusLabel(value as string)}
                        </Box>
                      </Box>
                    );
                  }}>
                  <MenuItem value="all">{t('invoice.allInvoices')}</MenuItem>
                  <MenuItem value={InvoiceStatus.UnderReview}>
                    {t('invoice.underReview')}
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.ProcessingPayment}>
                    {t('invoice.processingPayment')}
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.Collection}>
                    {t('invoice.collection')}
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.Paid}>
                    {t('invoice.paid')}
                  </MenuItem>
                </Select>
              </FormControl>
              <Button
                className={classes.filtersButton}
                startIcon={<FilterListIcon />}>
                {t('invoice.filters')}
              </Button>
            </Box>
            <IconButton>
              <MoreVertIcon />
            </IconButton>
          </Box>
          <DataGridPro
            loading={loading}
            rows={paginatedInvoices}
            getRowId={(row) => row.id}
            columns={columns as GridColDef[]}
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={(newSortModel) =>
              setSortModel(
                newSortModel as { field: string; sort: 'asc' | 'desc' }[],
              )
            }
            className={`${classes.grid} ${sortedInvoices.length === 0 ? 'empty' : ''}`}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            hideFooterRowCount
            hideFooter
            rowHeight={56}
            disableColumnMenu={true}
            disableColumnResize={true}
          />
          <TablePagination
            component="div"
            count={sortedInvoices.length}
            page={page - 1}
            onPageChange={(event, page) => setPage(page + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number.parseInt(event.target.value, 10));
              setPage(1);
            }}
          />
        </CardContent>
      </Card>
    </StyledBox>
  );
}
